import React, { useState, useEffect, forwardRef,
  useImperativeHandle, useMemo,  } from "react";
import {
  View,
  Text,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { useTranslation } from 'react-i18next';
import IdCard from "../../../../../../../../assets/images/Vector.svg";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import { capturePosthogEvent } from "../../../../../utils/analytics";
import { validateField, getFieldLabel, getFieldMax, getFieldMin } from "../../../../../helper/fieldHelper";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string | undefined;
};

export interface MapiEnterSenderNameStepHandle {
  submit: () => Promise<boolean>;
}

const MapiEnterSenderNameStep = forwardRef<MapiEnterSenderNameStepHandle, Props>(({ onValidationChange, testID }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    secondLastName: "",
    middleName: "",
  });

  const initialFirst = useSessionStore((s: SessionState) => (s as any).senderFirstName) || "";
  const initialMiddle = useSessionStore((s: SessionState) => (s as any).senderMiddleName) || "";
  const initialLast = useSessionStore((s: SessionState) => (s as any).senderLastName) || "";
  const initialSecondLast = useSessionStore((s: SessionState) => (s as any).senderSecondLastName) || "";
  const [firstName, setFirstName] = useState(initialFirst);
  const [middleName, setMiddleName] = useState(initialMiddle);
  const [lastName, setLastName] = useState(initialLast);
  const [secondLastName, setSecondLastName] = useState(initialSecondLast);
  const setSenderFirstName = useSessionStore((s: SessionState) => (s as any).setSenderFirstName);
  const setSenderMiddleName = useSessionStore((s: SessionState) => (s as any).setSenderMiddleName);
  const setSenderLastName = useSessionStore((s: SessionState) => (s as any).setSenderLastName);
  const setSenderSecondLastName = useSessionStore((s: SessionState) => (s as any).setSenderSecondLastName);
  const transactionFieldsData = useSessionStore((s: SessionState) => (s as any).mapiTransactionFieldsData);
  const fieldInfo: any[] = transactionFieldsData?.fieldInfo ?? [];

  const isValid = useMemo(() => {
    return (
      validateField(fieldInfo, "sender.name.firstName", firstName) === "" &&
      validateField(fieldInfo, "sender.name.middleName", middleName) === "" &&
      validateField(fieldInfo, "sender.name.lastName", lastName) === "" &&
      validateField(fieldInfo, "sender.name.secondLastName", secondLastName) ===
        ""
    );
  }, [fieldInfo, firstName, middleName, lastName, secondLastName]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const newErrors = {
        firstName: validateField(fieldInfo, "sender.name.firstName", firstName),
        middleName: validateField(fieldInfo, "sender.name.middleName", middleName),
        lastName: validateField(fieldInfo, "sender.name.lastName", lastName),
        secondLastName: validateField(fieldInfo, "sender.name.secondLastName", secondLastName),
      };
      setErrors(newErrors);
      const isValid = !Object.values(newErrors).some(Boolean);
      if (isValid) {
        capturePosthogEvent("Mapi Send Flow - Sender Name", {
          "data-mg-sender-firstname": firstName,
          "data-mg-sender-lastname": lastName,
        });
      }
      return isValid;
    },
  }));

  return (
    <KeyboardAvoidingView
      testID={testID}
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      <Text style={styles.title}>{t("SENDER_NAME_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_NAME_SUBTITLE")}</Text>

      <FloatingTextInputComponent
        testID="mapi-senderFirstName"
        label={getFieldLabel(fieldInfo, "sender.name.firstName")}
        value={firstName}
        isAutoFocus={true}
        maxLength={getFieldMax(fieldInfo, "sender.name.firstName") ? Number(getFieldMax(fieldInfo, "sender.name.firstName")) : undefined}
        minLength={getFieldMin(fieldInfo, "sender.name.firstName") ? Number(getFieldMin(fieldInfo, "sender.name.firstName")) : undefined}
        onChangeText={(val) => {
          setFirstName(val);
          setSenderFirstName?.(val);
          if (errors.firstName) setErrors({ ...errors, firstName: "" });
        }}
        errorText={errors.firstName}
      />
      <FloatingTextInputComponent
        testID="mapi-senderMiddleName"
        label={getFieldLabel(fieldInfo, "sender.name.middleName")}
        value={middleName}
        maxLength={getFieldMax(fieldInfo, "sender.name.middleName") ? Number(getFieldMax(fieldInfo, "sender.name.middleName")) : undefined}
        minLength={getFieldMin(fieldInfo, "sender.name.middleName") ? Number(getFieldMin(fieldInfo, "sender.name.middleName")) : undefined}
        onChangeText={(val) => {
          setMiddleName(val);
          setSenderMiddleName?.(val);
          if (errors.middleName) setErrors({ ...errors, middleName: "" });
        }}
        errorText={errors.middleName}
      />
      <FloatingTextInputComponent
        testID="mapi-senderLastName"
        label={getFieldLabel(fieldInfo, "sender.name.lastName")}
        value={lastName}
        maxLength={getFieldMax(fieldInfo, "sender.name.lastName") ? Number(getFieldMax(fieldInfo, "sender.name.lastName")) : undefined}
        minLength={getFieldMin(fieldInfo, "sender.name.lastName") ? Number(getFieldMin(fieldInfo, "sender.name.lastName")) : undefined}
        onChangeText={(val) => {
          setLastName(val);
          setSenderLastName?.(val);
          if (errors.lastName) setErrors({ ...errors, lastName: "" });
        }}
        errorText={errors.lastName}
      />
      <FloatingTextInputComponent
        testID="mapi-senderSecondLastName"
        label={getFieldLabel(fieldInfo, "sender.name.secondLastName")}
        value={secondLastName}
        maxLength={getFieldMax(fieldInfo, "sender.name.secondLastName") ? Number(getFieldMax(fieldInfo, "sender.name.secondLastName")) : undefined}
        minLength={getFieldMin(fieldInfo, "sender.name.secondLastName") ? Number(getFieldMin(fieldInfo, "sender.name.secondLastName")) : undefined}
        onChangeText={(val) => {
          setSecondLastName(val);
          setSenderSecondLastName?.(val);
          if (errors.secondLastName) setErrors({ ...errors, secondLastName: "" });
        }}
        errorText={errors.secondLastName}
      />

      <View style={styles.hintBox}>
        <IdCard width={24} height={24}/>
        <Text style={styles.hintText}>
          {t("MAKE_SURE_TO_USE_NAME_ON_YOUR_LEGAL_ID")}
        </Text>
      </View>
    </KeyboardAvoidingView>
  );
});

export default MapiEnterSenderNameStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500',
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 10,
    padding: 16,
    marginTop: 24,
    backgroundColor: Colors.white,
    marginBottom: 79
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: '400',
    color: Colors.darkGrey,
    lineHeight: 18,
    paddingLeft: 16
  },
});
